
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import List from '@/common/components/UI/List/index.vue'

// module components
import UsersActions from '@/modules/UsersModule/components/UsersActions/index.vue'
import UsersListItem from '@/modules/UsersModule/components/UsersListItem/index.vue'
import DispatcherSectorAttach from '@/modules/UsersModule/components/DispatcherSectorAttach/index.vue'

// types
import { UpdateUserPayloadT, UserPayloadT, UserT } from '@/core/types/users.types'
import { GatewayRoleT } from '@/core/types/gateway.types'

// realisations
import { users } from '@/core/realisations/users.realisation'
import { dispatchers_sectors } from '@/core/realisations/dispatchers_sectors.realisation'
import { gateway } from '@/core/realisations/gateway.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// composable
import useActions from '@/common/composable/useActions'
import useHelpers from '@/common/composable/useHelpers'
import useNotifications from '@/common/composable/useNotifications'

// constants
import { USERS_STATUS_OPTIONS } from '@/core/constants/users.constants'
import { companies } from '@/core/realisations/companies.realisation'
import { CompanyT } from '@/core/types/companies.types'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'the-input': TheInput,
        TheSelect,
        'failed-fetch': FailedFetch,
        list: List,
        'users-actions': UsersActions,
        'users-list-item': UsersListItem,
        'dispatcher-sector-attach': DispatcherSectorAttach,
    },
    setup() {
        const notifications = useNotifications()

        const state = reactive({
            users: new DataState<UserT>({}),
            search: undefined as string | undefined,
            phone: undefined as string | undefined,
            roleId: undefined as number | undefined,
            statusId: undefined as number | undefined,
            name: undefined as string | undefined,
            companyIds: [] as number[],
            roles: new DataState<GatewayRoleT>({}),
            companies: new DataState<CompanyT>({}),
        })

        const attach_state = reactive({
            user: null as UserT | null,
            is_opened: false,
            is_loading: false,
        })

        const { findItemIndexById } = useHelpers()

        //

        const loadUsers = async (page: number) => {
            state.users.pagination.current_page = page

            try {
                state.users.is_loading = true

                const response = await users.getList({
                    page,
                    email: state.search,
                    phone: state.phone,
                    roleId: state.roleId,
                    statusId: state.statusId,
                    name: state.name,
                    companyIds: state.companyIds,
                })

                state.users.list = response.data

                state.users.pagination.total_pages = response.meta.last_page

                state.users.is_failed = false
                state.users.failure.error_state.is_cancel = false
            } catch (error) {
                state.users.failure.catchError(error)

                if (!state.users.failure.error_state.is_cancel) {
                    state.users.is_failed = true
                }
            } finally {
                if (!state.users.failure.error_state.is_cancel) {
                    state.users.is_loading = false
                }
            }
        }

        //

        const loadRoles = async () => {
            try {
                state.roles.is_loading = true

                const response = await gateway.getRoles(1, { limit: 9999, isExtended: 0 })
                state.roles.list = response.data

                state.roles.pagination.total_pages = response.meta.last_page

                state.roles.is_failed = false
                state.roles.failure.error_state.is_cancel = false
            } catch (error) {
                state.roles.failure.catchError(error)

                if (!state.roles.failure.error_state.is_cancel) {
                    state.roles.is_failed = true
                }
            } finally {
                if (!state.roles.failure.error_state.is_cancel) {
                    state.roles.is_loading = false
                }
            }
        }

        //

        const reset = () => {
            state.search = undefined
            state.phone = undefined
            state.roleId = undefined
            state.statusId = undefined
            state.name = undefined
            state.companyIds = []

            loadUsers(1)
        }

        const { actions_state, startCreate, startUpdate, startDelete } = useActions<UserT>()

        const createUser = async (payload: UserPayloadT) => {
            try {
                actions_state.is_actions_loading = true

                await users.createUser(payload)
                await loadUsers(state.users.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ title: 'Ошибка при добавлении пользователя', error })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateUser = async ({ id, payload }: { id: number; payload: UpdateUserPayloadT }) => {
            try {
                actions_state.is_actions_loading = true

                await users.updateUser(id, payload)
                await loadUsers(state.users.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ title: 'Ошибка при сохранении пользователя', error })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onUpdateUserInCabinet = async ({
            id,
            payload,
        }: {
            id: number
            payload: UpdateUserPayloadT
        }) => {
            try {
                actions_state.is_actions_loading = true

                await users.updateUserInCabinet(id, payload)
                await loadUsers(state.users.pagination.current_page)

                notifications.notify({ title: 'Пользователь сохранен в кабинете', type: 'success' })
                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    title: 'Ошибка при сохранении пользователя в кабинете',
                    error,
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const deleteUser = async (user_id: number) => {
            try {
                actions_state.is_actions_loading = true

                await users.deleteUser(user_id)
                await loadUsers(state.users.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ title: 'Ошибка при удалении пользователя', error })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onStartAttach = (user: UserT) => {
            attach_state.user = { ...user }
            attach_state.is_opened = true
        }

        const onCancelAttach = () => {
            attach_state.is_opened = false
            attach_state.user = null
        }

        const onSubmitAttach = async (payload: { id: number; sectors: number[] }) => {
            try {
                attach_state.is_loading = true

                await dispatchers_sectors.attachUser(payload.id, payload.sectors)

                notifications.notify({ title: 'Сектора диспетчера привязаны', type: 'success' })
                onCancelAttach()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При привязке секторов диспетчера произошла ошибка',
                })
            } finally {
                attach_state.is_loading = false
            }
        }

        const loadCompanies = async () => {
            try {
                state.companies.is_loading = true

                const response = await companies.getAllList({
                    page: 1,
                    isExtended: 0,
                    limit: 9999,
                    statusId: 2,
                })
                state.companies.list = response.data

                state.companies.is_failed = false
            } catch (error) {
                state.companies.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка произошла ошибка',
                })
            } finally {
                state.companies.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadUsers(1)
            loadRoles()
            loadCompanies()
        })

        return {
            state,
            actions_state,
            attach_state,
            //
            loadUsers,
            //
            loadRoles,
            //
            startCreate,
            startUpdate,
            startDelete,
            createUser,
            updateUser,
            onUpdateUserInCabinet,
            deleteUser,
            reset,
            //
            onStartAttach,
            onCancelAttach,
            onSubmitAttach,
            loadCompanies,
            //
            USERS_STATUS_OPTIONS,
        }
    },
})
